import { Select } from 'antd'
import { t } from 'i18next'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useUsers } from '../hooks'
import { User } from '../store/reducers/userReducer'
import { WorkspaceLead } from '../store/reducers/workspaceReducer'

interface SelectOption {
  key: string
  value: string
  label: string
  disabled?: boolean
}

interface SelectUserProps {
  onChange?: (value: WorkspaceLead | undefined) => void
  disabled?: boolean
  initialValue?: string
  allUsers?: User[]
  placeholder?: string
}

const SelectUser = memo(
  ({
    onChange,
    disabled,
    initialValue,
    allUsers,
    placeholder,
  }: SelectUserProps) => {
    const { userList: defaultUsers, loading } = useUsers({})
    const [selectedUser, setSelectedUser] = useState<string | undefined>(
      initialValue
    )

    const options: SelectOption[] = useMemo(() => {
      const users =
        allUsers !== undefined && allUsers.length !== 0
          ? allUsers
          : defaultUsers
      return (users ?? []).map((user: User) => ({
        key: user.id,
        value: user.id,
        label: user.displayName,
      }))
    }, [defaultUsers, allUsers])

    const selectedUserLabel = useMemo(() => {
      if (!selectedUser) return undefined
      const option = options.find((opt) => opt.value === selectedUser)
      return option ? { value: selectedUser, label: option.label } : undefined
    }, [selectedUser, options])

    const handleChange = useCallback(
      (value: any) => {
        const userId = value?.value
        setSelectedUser(userId)
        if (onChange) {
          onChange({ name: value?.label, id: userId })
        }
      },
      [onChange]
    )

    useEffect(() => {
      setSelectedUser(initialValue)
    }, [initialValue])

    return (
      <Select
        size="large"
        style={{ width: '100%' }}
        options={options}
        disabled={disabled}
        value={selectedUserLabel}
        labelInValue
        loading={loading}
        onChange={handleChange}
        optionFilterProp="label"
        notFoundContent={loading ? t('loading') : t('noUsersFound')}
        placeholder={placeholder}
        showSearch
        filterOption={(input, option) =>
          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
        }
      />
    )
  }
)

export default SelectUser
